import { createRoute } from "@tanstack/solid-router";
import { lazy, Suspense } from "solid-js";
import { appRoute } from "./route";
import { AppErrorBoundary } from "@/components/ui/error-boundary";

const AssetDetailView = lazy(async () => {
  const m = await import("@/features/assets/components/asset-detail-view");
  return { default: m.AssetDetailView };
});

function AssetDetailPage() {
  const params = assetDetailRoute.useParams();
  return (
    <AppErrorBoundary region label="asset-detail">
      <Suspense fallback={null}>
        <AssetDetailView assetId={params().assetId} />
      </Suspense>
    </AppErrorBoundary>
  );
}

/**
 * Deep-linkable dialogs for asset detail. URL is source of truth
 * for which overlay is open, so each dialog has its own reproducible
 * link replicating the exact screen:
 *   `?edit=1`                  → edit asset
 *   `?dialog=service|reading|warranty`
 *   `?dialog=rule`             → add reminder
 *   `?rule=<id>`               → edit reminder (entity)
 *   `?confirm=delete`          → confirm delete
 */
export interface AssetDetailSearch {
  edit?: boolean;
  dialog?: "service" | "reading" | "rule" | "warranty";
  rule?: string;
  confirm?: "delete";
}

const ASSET_DETAIL_DIALOGS = ["service", "reading", "rule", "warranty"] as const;

function parseDialog(value: unknown): AssetDetailSearch["dialog"] {
  return ASSET_DETAIL_DIALOGS.find((d) => d === value);
}

export const assetDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/assets/$assetId",
  component: AssetDetailPage,
  validateSearch: (search: Record<string, unknown>): AssetDetailSearch => ({
    // Includes NUMBER 1: TanStack auto-parses `?edit=1` to number 1 (not string);
    // without this case param was discarded on boot and edit dialog did
    // not open via deep-link (CC-18).
    edit:
      search.edit === true || search.edit === 1 || search.edit === "1" || search.edit === "true"
        ? true
        : undefined,
    dialog: parseDialog(search.dialog),
    rule: typeof search.rule === "string" && search.rule.trim() !== "" ? search.rule : undefined,
    confirm: search.confirm === "delete" ? "delete" : undefined,
  }),
});
